import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  performanceMetricsService,
  HealthCheckDefinition,
  HealthReport,
  HealthArticle,
  HealthRefreshStatus,
} from '../../../../services/performanceMetricsService';
import { blogCandidatesService } from '../../../../services/blogCandidatesService';
import { blogRunsService } from '../../../../services/blogRunsService';
import { ArticleDetailPanel } from '../Performance/ArticleDetailPanel';
import { ChecksTab } from './ChecksTab';
import { ArticlesTab } from './ArticlesTab';
import { fmtDate } from './healthCheckHelpers';

type Tab = 'articles' | 'checks';

export const HealthChecksSection: React.FC<{
  onNavigateToArticle?: (candidateId: string) => void;
}> = ({ onNavigateToArticle }) => {
  const [tab, setTab] = useState<Tab>('articles');
  const [report, setReport] = useState<HealthReport | null>(null);
  const [checks, setChecks] = useState<HealthCheckDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshStatus, setRefreshStatus] = useState<HealthRefreshStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isRefreshing = refreshStatus?.status === 'running';

  // Load report + check definitions
  const loadReport = useCallback(async () => {
    try {
      const [reportResp, checksResp] = await Promise.all([
        performanceMetricsService.getHealthReport(),
        performanceMetricsService.getHealthChecks(),
      ]);
      setReport(reportResp);
      setChecks(checksResp.checks);
    } catch (e: any) {
      setError(e?.message || 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);

  // Poll refresh status every 5s when a refresh is in flight
  useEffect(() => {
    if (!isRefreshing) return;
    pollRef.current = setInterval(async () => {
      try {
        const status = await performanceMetricsService.getHealthRefreshStatus();
        setRefreshStatus(status);
        if (status.status === 'completed') {
          loadReport();
        }
      } catch {
        // Non-fatal
      }
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isRefreshing, loadReport]);

  const [refreshStartMsg, setRefreshStartMsg] = useState<string | null>(null);
  const [detailUrl, setDetailUrl] = useState<string | null>(null);
  const [startingRefreshUrl, setStartingRefreshUrl] = useState<string | null>(null);

  // Indexation refresh state
  const [indexationStatus, setIndexationStatus] = useState<{ status: string; step?: string; progress?: { done: number; total: number }; checked_at?: string } | null>(null);
  const indexationPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isIndexing = indexationStatus?.status === 'running';

  // Load initial indexation status
  useEffect(() => {
    performanceMetricsService.getIndexationStatus().then(setIndexationStatus).catch(() => {});
  }, []);

  // Poll indexation status while running
  useEffect(() => {
    if (!isIndexing) return;
    indexationPollRef.current = setInterval(async () => {
      try {
        const status = await performanceMetricsService.getIndexationStatus();
        setIndexationStatus(status);
        if (status.status === 'completed') {
          loadReport();
        }
      } catch { /* non-fatal */ }
    }, 5000);
    return () => { if (indexationPollRef.current) clearInterval(indexationPollRef.current); };
  }, [isIndexing, loadReport]);

  const handleIndexationRefresh = useCallback(async () => {
    setIndexationStatus({ status: 'running', step: 'Starting...' });
    try {
      await performanceMetricsService.refreshIndexation();
      const status = await performanceMetricsService.getIndexationStatus();
      setIndexationStatus(status);
    } catch (e: any) {
      setIndexationStatus(null);
      setError(`Failed to start indexation check: ${e?.message || 'unknown'}`);
    }
  }, []);

  const handleStartRefresh = useCallback(async (article: HealthArticle) => {
    setRefreshStartMsg(null);
    setStartingRefreshUrl(article.url);
    try {
      const candidate = await blogCandidatesService.create({
        candidate_name: article.title,
        icp: article.category || 'Business Professionals',
        funnel_stage: 'Top',
        focus: 'B2B',
        wordpress_category_slug: (article.category || 'uncategorized').toLowerCase().replace(/\s+/g, '-'),
        status: 'active',
      });

      await blogRunsService.create(candidate.candidate_id, {
        pipeline_type: 'refresh',
        inventory_entry: {
          url: article.url,
          title: article.title,
          category: article.category,
          published_at: article.published_at,
          word_count: article.word_count,
        },
        performance_data: {
          clicks: article.clicks,
          impressions: article.impressions,
          avg_position: article.avg_position,
          ctr: article.ctr,
          health_issues: article.checks
            .filter((c) => c.severity > 0)
            .map((c) => `${c.check_id}: ${c.detail}`),
        },
      });

      if (onNavigateToArticle) {
        onNavigateToArticle(candidate.candidate_id);
      } else {
        setRefreshStartMsg(`Refresh started for "${article.title}". Go to Article Generation to see it.`);
      }
    } catch (e: any) {
      setRefreshStartMsg(`Failed to start refresh: ${e?.message || 'unknown error'}`);
    } finally {
      setStartingRefreshUrl(null);
    }
  }, [onNavigateToArticle]);

  const handleRefresh = useCallback(async () => {
    setRefreshStatus({ status: 'running', step: 'Starting...', phase: 'starting' });
    try {
      await performanceMetricsService.refreshHealth();
      const status = await performanceMetricsService.getHealthRefreshStatus();
      setRefreshStatus(status);
    } catch (e: any) {
      setRefreshStatus(null);
      setError(`Failed to start refresh: ${e?.message || 'unknown'}`);
    }
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Health Checks</h1>
          <p className="text-sm text-gray-500 mt-1">
            Each article is scored across 10 checks (position, CTR, indexation, links, content freshness, keyword drift, and more).
            Scores: <strong className="text-green-600">0-24</strong> healthy,{' '}
            <strong className="text-yellow-600">25-74</strong> minor,{' '}
            <strong className="text-amber-600">75-149</strong> needs attention,{' '}
            <strong className="text-red-600">150+</strong> urgent.
            {report?.generated_at && <> Last run: {fmtDate(report.generated_at)}.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleIndexationRefresh}
            disabled={isIndexing || isRefreshing}
            className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isIndexing ? 'Checking...' : 'Check Indexation'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || isIndexing}
            className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isRefreshing ? 'Running...' : 'Run Health Checks'}
          </button>
        </div>
      </div>

      {/* Indexation last-checked date */}
      {indexationStatus?.checked_at && !isIndexing && (
        <div className="text-xs text-gray-400 mb-2">
          Last indexation check: {fmtDate(indexationStatus.checked_at)}
        </div>
      )}

      {/* Indexation progress */}
      {isIndexing && indexationStatus && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-3">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium">{indexationStatus.step || 'Checking indexation...'}</div>
              {indexationStatus.progress && indexationStatus.progress.total > 0 && (
                <div className="mt-1">
                  <div className="w-full bg-blue-100 rounded-full h-1.5">
                    <div
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (indexationStatus.progress.done / Math.max(indexationStatus.progress.total, 1)) * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-blue-600 mt-0.5">
                    {indexationStatus.progress.done} / {indexationStatus.progress.total}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isRefreshing && refreshStatus && (
        <div className="bg-cyan-50 border border-cyan-200 text-cyan-800 px-4 py-3 rounded mb-3">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-cyan-500 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium">{refreshStatus.step || 'Processing...'}</div>
              {refreshStatus.progress && (
                <div className="mt-1">
                  <div className="w-full bg-cyan-100 rounded-full h-1.5">
                    <div
                      className="bg-cyan-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (refreshStatus.progress.done / Math.max(refreshStatus.progress.total, 1)) * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-cyan-600 mt-0.5">
                    {refreshStatus.progress.done} / {refreshStatus.progress.total}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {refreshStartMsg && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded mb-3 text-sm">
          {refreshStartMsg}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        {([['articles', 'Articles'], ['checks', 'Check Definitions']] as const).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t
                ? 'border-cyan-500 text-cyan-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500" />
        </div>
      ) : tab === 'checks' ? (
        <ChecksTab checks={checks} />
      ) : (
        <ArticlesTab report={report} checks={checks} onStartRefresh={handleStartRefresh} onViewDetails={(url) => setDetailUrl(url)} startingRefreshUrl={startingRefreshUrl} />
      )}

      {detailUrl && (
        <ArticleDetailPanel url={detailUrl} onClose={() => setDetailUrl(null)} />
      )}
    </div>
  );
};
